import { Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';

interface AiDraftBannerProps {
  /** Whether the document still reads as AI-original (no human edits yet).
   *  The host editor flips this to false on the first user keystroke. */
  showing: boolean;
  className?: string;
}

/**
 * Provenance line shown when the document arrived as an AI-generated
 * draft and the practitioner hasn't touched it yet. The label sits in a
 * small mint-tinted pill — a discrete, editorial badge — and the
 * explanatory copy trails after it as soft secondary text. Disappears
 * on first keystroke so the practitioner's voice owns the page.
 */
export function AiDraftBanner({ showing, className }: AiDraftBannerProps) {
  if (!showing) return null;
  return (
    <div className={cn('flex items-center gap-2.5 leading-none', className)}>
      <span className="bg-mint-paper/60 ring-primary-200/40 text-accent-ink inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ring-1">
        <Sparkles
          className="text-primary-600 h-3.5 w-3.5 shrink-0"
          strokeWidth={1.75}
          aria-hidden="true"
        />
        <span className="font-display text-[11.5px] font-medium tracking-tight italic">
          AI-drafted
        </span>
      </span>
      <span
        aria-hidden="true"
        className="text-accent-ink/20 font-display -mx-0.5 text-[14px] leading-none italic select-none"
      >
        —
      </span>
      <span className="text-[12.5px] leading-snug tracking-tight text-neutral-500">
        edit anything that doesn’t match the room, then seal.
      </span>
    </div>
  );
}
